/**
 * Material Detail Modal
 *
 * Shows detailed information about a learning material
 * with assignment stats and actions.
 */

import { useState } from 'react';
import {
  X,
  Video,
  FileText,
  Download,
  Users,
  Trash2,
  Link2,
  Clock,
  Calendar,
  Loader2,
  AlertTriangle,
  PlayCircle,
} from 'lucide-react';
import { useMaterial, useDeleteMaterial } from '../../../hooks/useLearning';

interface MaterialDetailModalProps {
  materialId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onAssign: (materialId: string, materialTitle: string) => void;
}

export function MaterialDetailModal({
  materialId,
  isOpen,
  onClose,
  onAssign,
}: MaterialDetailModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: material, isLoading } = useMaterial(materialId);
  const deleteMaterial = useDeleteMaterial();

  const handleDelete = async () => {
    if (!materialId) return;
    try {
      await deleteMaterial.mutateAsync(materialId);
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return null;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes} min`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  const isVideo = material?.materialType === 'video';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Material Details</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-guild-green" />
            </div>
          ) : material ? (
            <div className="space-y-6">
              {/* Header Row */}
              <div className="flex items-start gap-5">
                <div
                  className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl ${
                    isVideo
                      ? 'bg-gradient-to-br from-violet-500/10 to-violet-600/20 text-violet-600'
                      : 'bg-gradient-to-br from-amber-500/10 to-amber-600/20 text-amber-600'
                  }`}
                >
                  {isVideo ? <Video size={32} strokeWidth={1.5} /> : <FileText size={32} strokeWidth={1.5} />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">{material.title}</h3>
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        isVideo
                          ? 'bg-violet-100 text-violet-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {isVideo ? 'Video' : 'Document'}
                    </span>
                    {material.duration && isVideo && (
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock size={14} />
                        {formatDuration(material.duration)}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar size={14} />
                      Added {formatDate(material.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {material.description && (
                <div className="rounded-xl bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">{material.description}</p>
                </div>
              )}

              {/* Linked SOP */}
              {material.sop && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Link2 size={16} className="text-gray-400" />
                  <span>Linked to SOP:</span>
                  <span className="font-medium text-guild-green">{material.sop.title}</span>
                </div>
              )}

              {/* Assignment Stats */}
              <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50/50 to-white p-5">
                <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Assignment Statistics
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="mb-1 text-2xl font-bold text-gray-900">
                      {material.assignmentStats.total}
                    </div>
                    <div className="text-xs text-gray-500">Total Assigned</div>
                  </div>
                  <div className="text-center">
                    <div className="mb-1 text-2xl font-bold text-emerald-600">
                      {material.assignmentStats.completed}
                    </div>
                    <div className="text-xs text-gray-500">Completed</div>
                  </div>
                  <div className="text-center">
                    <div className="mb-1 text-2xl font-bold text-blue-600">
                      {material.assignmentStats.inProgress}
                    </div>
                    <div className="text-xs text-gray-500">In Progress</div>
                  </div>
                </div>

                {/* Completion Rate Bar */}
                {material.assignmentStats.total > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between text-xs">
                      <span className="text-gray-500">Completion Rate</span>
                      <span className="font-semibold text-emerald-600">
                        {material.assignmentStats.completionRate ?? 0}%
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                        style={{ width: `${material.assignmentStats.completionRate ?? 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Button for Videos */}
              {isVideo && material.downloadUrl && (
                <a
                  href={material.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                >
                  <PlayCircle size={18} />
                  Preview Video
                </a>
              )}
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-gray-500">
              Material not found
            </div>
          )}
        </div>

        {/* Footer */}
        {material && !showDeleteConfirm && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              <Trash2 size={16} />
              Delete
            </button>
            <div className="flex gap-3">
              <a
                href={material.downloadUrl}
                download
                className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                <Download size={16} />
                Download
              </a>
              <button
                onClick={() => onAssign(material.id, material.title)}
                className="flex items-center gap-2 rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
              >
                <Users size={16} />
                Assign to Operators
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="border-t border-gray-100 px-6 py-4">
            <div className="mb-4 flex items-start gap-3 rounded-xl bg-red-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div>
                <p className="font-medium text-red-800">Delete this material?</p>
                <p className="mt-1 text-sm text-red-600">
                  This will remove the material and all its assignments. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMaterial.isPending}
                className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMaterial.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Material
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
